import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useReveal from "../../hooks/useReveal";
import "./Home.css";

interface OfferItem {
  title: string;
}

interface WhyUsSlide {
  heading: string;
  text: string;
}

interface ProcessStep {
  num: string;
  name: string;
  desc: string;
}

const serviceImages: string[] = [
  "/images/offer-workspace.jpg",
  "/images/offer-education.jpg",
  "/images/offer-hotel.jpg",
  "/images/offer-flooring.jpg",
  "/images/offer-ceiling.jpg",
];

function Home() {
  const { t } = useTranslation();
  const [slideIndex, setSlideIndex] = useState(0);
  const slides = t("whyUs.slides", { returnObjects: true }) as WhyUsSlide[];
  const offerItems = t("offer.items", { returnObjects: true }) as OfferItem[];
  const processSteps = t("process.steps", {
    returnObjects: true,
  }) as ProcessStep[];
  useReveal();

  const nextSlide = useCallback(() => {
    setSlideIndex((prev) =>
      slides.length === 0 ? 0 : (prev + 1) % slides.length,
    );
  }, [slides.length]);

  const prevSlide = () => {
    setSlideIndex((prev) =>
      slides.length === 0 ? 0 : (prev - 1 + slides.length) % slides.length,
    );
  };

  useEffect(() => {
    if (slides.length === 0) {
      return undefined;
    }

    const interval = window.setInterval(nextSlide, 5000);
    return () => window.clearInterval(interval);
  }, [nextSlide, slides.length]);

  const activeSlide = slides[slideIndex] || { heading: "", text: "" };

  return (
    <main>
      {/* Hero */}
      <section className="hero">
        <div className="hero-image">
          <img
            src={`${process.env.PUBLIC_URL}/images/hero-blueprint.jpg`}
            alt=""
          />
        </div>
        <div className="hero-content">
          <h1 className="hero-title">{t("hero.title")}</h1>
          <p className="hero-text">{t("hero.text")}</p>
          <div>
            <Link to="/about" className="btn btn-outline btn-sm">
              {t("hero.cta")}
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Us */}
      <section className="section contact-home-section">
        <div className="container">
          <h2 className="contact-home-title reveal">{t("contact.subtitle")}</h2>
          <div className="contact-home-info reveal">
            <div className="contact-home-col">
              <h3>{t("footer.addressTitle")}</h3>
              <p>{t("footer.address")}</p>
            </div>
            <div className="contact-home-col">
              <h3>{t("footer.contactTitle")}</h3>
              <p>
                <a href={`tel:${t("footer.phone")}`}>{t("footer.phone")}</a>
              </p>
              <p>
                <a href={`mailto:${t("footer.email")}`}>{t("footer.email")}</a>
              </p>
              <div className="contact-home-social">
                <a
                  href="https://www.linkedin.com/company/fcsco/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    fill="currentColor"
                  >
                    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.8v8.37h2.8v-4.67c0-.25.02-.5.1-.68a1.14 1.14 0 0 1 1-.77c.76 0 1 .52 1 1.42v4.7h2.8M6.5 8.37a1.37 1.37 0 0 0 1.37-1.37A1.37 1.37 0 0 0 6.5 5.63 1.37 1.37 0 0 0 5.13 7 1.37 1.37 0 0 0 6.5 8.37m1.4 10.13V10h-2.8v8.5h2.8Z" />
                  </svg>
                </a>
                <a
                  href="https://wa.me/966595496463"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    fill="currentColor"
                  >
                    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.87 9.87 0 0 0 4.74 1.21c5.46 0 9.91-4.45 9.91-9.91A9.85 9.85 0 0 0 12.04 2m0 18.15a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.24-8.24a8.2 8.2 0 0 1 8.24 8.24c0 4.54-3.7 8.24-8.24 8.24m4.52-6.16c-.25-.13-1.47-.72-1.69-.8-.23-.09-.4-.13-.56.12-.17.25-.64.8-.78.97-.15.17-.29.19-.54.06-.25-.12-1.05-.38-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.51.11-.11.25-.29.37-.43.12-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.13-.56-1.35-.77-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.06-.1-.23-.16-.48-.29Z" />
                  </svg>
                </a>
                {/* <a href="https://facebook.com" aria-label="Facebook">
                  f
                </a>
                <a href="https://twitter.com" aria-label="Twitter">
                  t
                </a> */}
              </div>
            </div>
            <div className="contact-home-col">
              <h3>{t("footer.hoursTitle")}</h3>
              <p>{t("footer.hours1")}</p>
              <p>{t("footer.hours2")}</p>
            </div>
          </div>
          <div className="contact-home-map reveal">
            <iframe
              title="FCS Location"
              src="https://www.google.com/maps?cid=10722254558332601632&output=embed"
              width="100%"
              height="350"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
            <div className="contact-home-map-actions">
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=24.7037057,46.6821346"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline btn-sm"
              >
                {t("footer.getDirections")}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="section offer-section">
        <h2 className="section-title reveal">{t("offer.title")}</h2>
        <div className="offer-grid">
          {offerItems.map((item, i) => (
            <div key={i} className={`offer-card reveal reveal-delay-${i + 1}`}>
              <img src={serviceImages[i]} alt={item.title} />
              <span className="offer-card-title">{item.title}</span>
            </div>
          ))}
        </div>
        <div className="offer-cta reveal">
          <Link to="/services" className="btn btn-outline btn-sm">
            {t("offer.learnMore")}
          </Link>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section why-section">
        <div className="container">
          <h2 className="section-title reveal">{t("whyUs.title")}</h2>
        </div>
        <div className="why-carousel reveal">
          <div className="why-slide-bg">
            <img
              src={`${process.env.PUBLIC_URL}/images/service-workspace.jpg`}
              alt=""
            />
            <div className="why-slide-overlay"></div>
          </div>
          <div className="why-slide-content">
            <h3>{activeSlide.heading}</h3>
            <p>{activeSlide.text}</p>
          </div>
          <button
            className="why-arrow why-arrow-left"
            onClick={prevSlide}
            aria-label="Previous"
          >
            &#8249;
          </button>
          <button
            className="why-arrow why-arrow-right"
            onClick={nextSlide}
            aria-label="Next"
          >
            &#8250;
          </button>
          <div className="why-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                className={`why-dot ${i === slideIndex ? "active" : ""}`}
                onClick={() => setSlideIndex(i)}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="section process-section">
        <div className="container">
          <h2 className="section-title reveal">{t("process.title")}</h2>
          <div className="process-cards">
            {processSteps.map((step, i) => (
              <div
                key={i}
                className={`process-card reveal reveal-delay-${i + 1}`}
              >
                <h4>
                  {step.num} {step.name}
                </h4>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

export default Home;
